'use client';
import { useState } from 'react';
import { ScoreReturnChart } from './ScoreReturnChart';
import { HorizonICTable } from './HorizonICTable';

// One-time explainer for what the composite score actually means.
// Sticky note: this is the question every PM new to the model asks.
export function CompositeExplainer() {
  const [open, setOpen] = useState(false);
  return (
    <section className={`mb-6 rounded-lg border ${open ? 'border-amber-700/40 bg-amber-950/10' : 'border-[#1e293b] bg-[#0d1117]'}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-5 py-3 flex items-center justify-between text-left text-[12px] tracking-wide uppercase text-[#94a3b8] hover:text-white transition-colors"
      >
        <span className="flex items-center gap-2">
          <span className="text-amber-400">{open ? '▾' : '▸'}</span>
          Reading composite scores
        </span>
        <span className="text-[10px] text-[#64748b] normal-case tracking-normal">
          {open ? 'click to collapse' : 'first time? open this'}
        </span>
      </button>
      {open ? (
        <div className="px-5 pb-5 pt-1 text-[13px] text-[#cbd5e1] leading-relaxed space-y-4 max-w-3xl">
          <p>
            The <strong className="text-white">composite</strong> is one number per stock per day.
            Range roughly <span className="font-mono text-amber-400">−6</span> to{' '}
            <span className="font-mono text-amber-400">+6.7</span>. Higher is better.
          </p>

          <table className="w-full text-[12px] border-collapse">
            <thead>
              <tr className="text-[10px] text-[#64748b] uppercase tracking-wide border-b border-[#1e293b]">
                <th className="text-left py-1.5 pr-3 w-24">Composite</th>
                <th className="text-left py-1.5">Reading</th>
              </tr>
            </thead>
            <tbody>
              <Row range="≥ 4" reading="Very high composite. The best names in the 1099-stock universe. Rare — needs high quality AND cheap valuation together." />
              <Row range="3 to 4" reading="High composite. Strong buy candidate. Almost certainly in the top 10 today." />
              <Row range="2 to 3" reading="Moderate composite. Solid name. Most open ideas used to live here, but the new desk bar requires ≥3 to surface." />
              <Row range="1 to 2" reading="Low composite. Not a buy. Watch the direction — improving from here can promote it." />
              <Row range="0 to 1" reading="Very low composite. The model is neutral-to-cool." />
              <Row range="< 0" reading="Sell. Expensive AND/OR low quality. The penalty for expensive kicks in below zero." />
            </tbody>
          </table>

          <p>
            <strong className="text-white">Comparing names.</strong> If NVDA composite is{' '}
            <span className="font-mono text-amber-400">1.5</span> and APP is{' '}
            <span className="font-mono text-amber-400">3.0</span>, the model has roughly twice the conviction in APP today. APP is in the top tier; NVDA is not.
          </p>

          <p>
            <strong className="text-white">Direction also matters.</strong> A name at composite 2.5 that is improving is a stronger candidate than one parked at 3.0 going nowhere. That is why the desk also looks at the recent 5-day and 20-day change.
          </p>

          <details className="text-[12px] text-[#94a3b8]">
            <summary className="cursor-pointer hover:text-white">The math <span className="text-[#64748b]">(α = 1.25, β = 0.90, shift = 0.5)</span></summary>
            <div className="mt-2 pl-4 border-l border-[#1e293b] space-y-1 leading-relaxed">
              <div>Scale each axis: <span className="font-mono text-amber-400">q′ = 1.25·Q</span>, <span className="font-mono text-amber-400">v′ = 0.90·(V + 0.5)</span>.</div>
              <div>When v′ ≥ 0: <span className="font-mono text-amber-400">composite = √(q′² + v′²)</span> — both axes reward quadratically.</div>
              <div>When v′ &lt; 0: <span className="font-mono text-amber-400">composite = q′ + v′</span> — more than 0.5σ expensive subtracts linearly.</div>
              <div>
                The asymmetry is the point. Cheap-and-quality is doubly rewarded; expensive monotonically discounts the score.
                The 0.5σ shift lets a high-quality compounder trade at a modest premium to peers and still earn the geometric reward.
              </div>
            </div>
          </details>

          {/* Empirical score → return relationship */}
          <div className="pt-4 border-t border-[#1e293b]">
            <h3 className="text-[12px] text-white font-semibold tracking-wide uppercase mb-3">
              Score → forward return (empirical, annual)
            </h3>
            <ScoreReturnChart />
          </div>

          {/* Velocity / acceleration horizon IC sweep */}
          <div className="pt-4 border-t border-[#1e293b]">
            <h3 className="text-[12px] text-white font-semibold tracking-wide uppercase mb-3">
              Δcomposite over which window? (level vs velocity vs acceleration)
            </h3>
            <HorizonICTable />
          </div>
        </div>
      ) : null}
    </section>
  );
}

function Row({ range, reading }: { range: string; reading: string }) {
  return (
    <tr className="border-b border-[#1e293b]">
      <td className="py-1.5 pr-3 font-mono text-amber-400 align-top">{range}</td>
      <td className="py-1.5 text-[#cbd5e1]">{reading}</td>
    </tr>
  );
}
